import {faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import {range} from "d3-array";
import React from "react";

import {ESampleCopiableValues} from "@shared/sop/models";
import {ISelectValue} from "@toolbox/button-like/models";
import {EDeviceClasses, trimId} from "@toolbox/models";
import {ILocalizedText} from "@translate/models";
import cellTypes from "../../../services/cell-types";
import {
    ENormalizationType,
    ICellTypeModel,
    IChannelModel,
    ICycleModel,
} from "../../models";

import Copy from "@shared/sop/Copy";
import Tooltip from "@toolbox/building-blocks/Tooltip";
import InlineSelect from "@toolbox/button-like/InlineSelect";
import {tableStriped} from "@toolbox/functions/table";
import T, {intl2Str} from "@translate/T";
import {checkInvalidLightfactor} from "../cycles/CycleRow";

export function standardCalibrationEmptyCellType(cellType: number) {
    const name = cellTypes.getNamebyId(cellType).toLowerCase();
    if (
        name.includes("standard") ||
        name.includes("calibration") ||
        name.includes("empty")
    ) {
        return ENormalizationType.EmptyChannel;
    }

    return ENormalizationType.EmptyCuvette;
}

export function convertCellType(id: number): ISelectValue {
    const name = cellTypes.getNamebyId(id);

    return {
        id: id.toString(),
        name: () => name,
    };
}

export function convertNormalizationType(
    value: ENormalizationType,
): ISelectValue {
    let name: ILocalizedText = (intl) => intl2Str(intl, "None");

    switch (value) {
        case ENormalizationType.EmptyChannel:
            name = (intl) => intl2Str(intl, "Empty channel");
            break;

        case ENormalizationType.EmptyCuvette:
            name = (intl) => intl2Str(intl, "Empty cuvette");
            break;
    }

    return {id: value.toString(), name};
}

export function getCellTypeToolTip(
    cellType: number,
    isAllowed: boolean,
    filtrationModule: boolean,
    isUnbalanced?: boolean,
    isMissingCounterpart?: boolean,
    sscMixedUp?: boolean,
) {
    let tooltip;
    let warning = false;
    if (!isAllowed) {
        tooltip = <T>Incompatible cell type</T>;
    } else if (sscMixedUp) {
        tooltip = <T>SSC cell types cannot be mixed with non-SSC types</T>;
    } else if (isUnbalanced) {
        tooltip = <T>Weight is unbalanced</T>;
    } else if (isMissingCounterpart) {
        warning = true;
        tooltip = (
            <T>
                Counterweight is missing, do not forget to add a physical
                reference
            </T>
        );
    } else if (
        !filtrationModule &&
        cellTypes.onlyFiltCellTypeIds.includes(cellType)
    ) {
        warning = true;
        tooltip = <T>SOP not marked for filtration measuring module</T>;
    } else if (
        filtrationModule &&
        ![
            ...cellTypes.filtCellTypeIds,
            ...cellTypes.onlyFiltCellTypeIds,
        ].includes(cellType)
    ) {
        warning = true;
        tooltip = (
            <T>Cell type not compatible with filtration measuring module</T>
        );
    }

    return {tooltip, warning};
}

function getNormTypeToolTip(
    value: IChannelModel,
    cycles: ICycleModel[],
    isNormUnbalanced: boolean,
) {
    let tooltip;

    tooltip = checkInvalidLightfactor(
        [{normalizationType: value.normalizationType}],
        cycles,
    );
    if (tooltip) {
        return tooltip;
    } else if (isNormUnbalanced) {
        tooltip = <T>Weight is unbalanced</T>;
    }

    return tooltip;
}

interface ISampleRowProps {
    idSuffix: number;
    value: IChannelModel;
    cycles: ICycleModel[];

    capacity: number;
    filteredCellTypes: ICellTypeModel[];

    canRemove: boolean;
    deviceClass: EDeviceClasses;
    filtrationModule: boolean;
    hasMany: boolean;
    isDuplicated: boolean;
    isMissingCounterpart: boolean;
    isNormUnbalanced: boolean;
    isUnbalanced: boolean;
    showNotes: boolean;
    sscMixedUp: boolean;

    onChange(
        updated: IChannelModel,
        old: IChannelModel,
        shallUpdateName?: boolean,
    ): void;
    onCopied(source: IChannelModel, values: ESampleCopiableValues): void;
    onRemoved(value: IChannelModel): void;
}

class SampleRow extends React.PureComponent<ISampleRowProps> {
    @boundMethod
    public updatePosition(position: number) {
        const {value, onChange} = this.props;

        onChange({...value, position}, value, true);
    }

    @boundMethod
    public updateName(e: React.ChangeEvent<HTMLInputElement>) {
        const {value, onChange} = this.props;
        const updated: IChannelModel = {...value, name: e.target.value};

        onChange(updated, value);
    }

    @boundMethod
    public updateNotes(e: React.ChangeEvent<HTMLInputElement>) {
        const {value, onChange} = this.props;
        const updated = {...value, notes: e.target.value};

        onChange(updated, value);
    }

    @boundMethod
    public updateCellType(cellType: number) {
        const {onChange, value, deviceClass} = this.props;

        let normalizationType = value.normalizationType;
        if (deviceClass === EDeviceClasses.XRay) {
            normalizationType = standardCalibrationEmptyCellType(cellType);
        }

        onChange({...value, cellType, normalizationType}, value);
    }

    @boundMethod
    public updateNormalizationType(normalizationType: ENormalizationType) {
        const {onChange, value} = this.props;

        onChange({...value, normalizationType}, value);
    }

    @boundMethod
    public removeSample(e: React.SyntheticEvent) {
        e.preventDefault();

        const {onRemoved, value} = this.props;
        onRemoved(value);
    }

    public render() {
        return (
            <React.Fragment>
                {this.renderMain()}
                {this.renderNotes()}
            </React.Fragment>
        );
    }

    private renderMain() {
        const {idSuffix} = this.props;

        return (
            <tr id={tableStriped(idSuffix)}>
                {this.renderPosition()}
                {this.renderName()}
                {this.renderCellType()}
                {this.renderNormalizationType()}
                {this.renderButtons()}
            </tr>
        );
    }

    private renderPosition() {
        const {value, capacity, isDuplicated, idSuffix} = this.props;

        let tooltip;
        if (isDuplicated) {
            tooltip = <T>Duplicated</T>;
        }

        return (
            <td>
                <Tooltip content={tooltip}>
                    <InlineSelect<number>
                        idSuffix={`count-samples-${idSuffix}`}
                        invalid={isDuplicated}
                        selected={value.position}
                        onSelected={this.updatePosition}
                        values={range(1, capacity + 1)}
                    />
                </Tooltip>
            </td>
        );
    }

    private renderName() {
        const {idSuffix, value} = this.props;
        const {name} = value;

        return (
            <td>
                <input
                    type="text"
                    id={`sample-name-${idSuffix}-${trimId}`}
                    autoComplete="off"
                    className="form-control"
                    required={true}
                    value={name}
                    onChange={this.updateName}
                />
            </td>
        );
    }

    private renderCellType() {
        const {
            filteredCellTypes,
            filtrationModule,
            idSuffix,
            isMissingCounterpart,
            isUnbalanced,
            sscMixedUp,
            value,
        } = this.props;

        const cellType = value.cellType as number;
        const isAllowed = !!filteredCellTypes.find((x) => x.id === cellType);
        const {tooltip, warning} = getCellTypeToolTip(
            cellType,
            isAllowed,
            filtrationModule,
            isUnbalanced,
            isMissingCounterpart,
            sscMixedUp,
        );

        return (
            <td>
                <Tooltip content={tooltip}>
                    <InlineSelect<number>
                        idSuffix={`cell-type-${idSuffix}`}
                        invalid={isUnbalanced || !isAllowed || sscMixedUp}
                        invalidWarning={warning}
                        selected={cellType}
                        onSelected={this.updateCellType}
                        convert={convertCellType}
                        values={filteredCellTypes.map((x) => x.id)}
                    />
                </Tooltip>
            </td>
        );
    }

    private renderNormalizationType() {
        const {idSuffix, value, cycles, isNormUnbalanced} = this.props;

        const title: ILocalizedText = (intl) =>
            intl2Str(intl, "Cuvette as selected for measurement.");

        const tooltip = getNormTypeToolTip(value, cycles, isNormUnbalanced);
        return (
            <td>
                <Tooltip content={tooltip}>
                    <InlineSelect<ENormalizationType>
                        idSuffix={`normalization-type-${idSuffix}`}
                        invalid={!!tooltip}
                        title={title}
                        selected={value.normalizationType}
                        onSelected={this.updateNormalizationType}
                        convert={convertNormalizationType}
                        values={
                            Object.values(ENormalizationType).filter(
                                (x) => typeof x !== "string",
                            ) as ENormalizationType[]
                        }
                    />
                </Tooltip>
            </td>
        );
    }

    private renderNotes() {
        const {showNotes, value, idSuffix} = this.props;

        if (!showNotes) {
            return null;
        }

        return (
            <tr id={tableStriped(idSuffix)}>
                <td className="align-middle">
                    <span className="font-weight-bold">
                        <T>Notes</T>
                    </span>
                </td>
                <td colSpan={4}>
                    <input
                        type="text"
                        id={`notes-${idSuffix}`}
                        className="form-control"
                        aria-label="sample-notes"
                        autoComplete="off"
                        value={value.notes}
                        onChange={this.updateNotes}
                    />
                </td>
            </tr>
        );
    }

    private renderButtons() {
        const {canRemove, onCopied, value, hasMany} = this.props;

        if (!hasMany) {
            return null;
        }

        return (
            <td>
                <div className="btn-group">
                    <Copy
                        cellStampTitle={<T>Cell type</T>}
                        value={value}
                        needNorm={true}
                        onCopied={onCopied}
                    />
                    <button
                        type="button"
                        className="btn btn-danger"
                        disabled={!canRemove}
                        id={"remove-" + value.position}
                        onClick={this.removeSample}
                    >
                        <FontAwesomeIcon icon={faTrash} fixedWidth={true} />
                    </button>
                </div>
            </td>
        );
    }
}

export default SampleRow;
